import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';

interface Story {
  key: string;
  year: number;
  color: string;
  imgExt: string;
  imgInt: string;
}

const IMG = 'assets/img/quienes-somos/';

@Component({
  selector: 'app-quienes-somos',
  templateUrl: './quienes-somos.component.html',
  styleUrls: ['./quienes-somos.component.scss'],
})
export class QuienesSomosComponent implements AfterViewInit, OnDestroy {
  // Datos no traducibles (año, color e imágenes). El texto vive en i18n: about.stories.<key>.*
  readonly stories: Story[] = [
    { key: 'earlyna', year: 2026, color: '#A39B4B', imgExt: IMG + 'banner_earlyna_ext.webp', imgInt: IMG + 'banner_earlyna_int.webp' },
    { key: 'valleygold', year: 2025, color: '#004434', imgExt: IMG + 'banner_valleygold_ext.png', imgInt: IMG + 'banner_valleygold_int.png' },
    { key: 'ciopora2023', year: 2023, color: '#A39B4B', imgExt: IMG + 'banner_ciopora_ext_2023.webp', imgInt: IMG + 'banner_ciopora_int_2023.webp' },
    { key: 'bollo', year: 2022, color: '#004434', imgExt: IMG + 'banner_bollo_ext.webp', imgInt: IMG + 'banner_bollo_int.webp' },
    { key: 'clemengold', year: 2021, color: '#A39B4B', imgExt: IMG + 'banner_clemengold_ext.webp', imgInt: IMG + 'banner_clemengold_int.webp' },
    { key: 'ciopora', year: 2019, color: '#004434', imgExt: IMG + 'banner_ciopora_ext.webp', imgInt: IMG + 'banner_ciopora_int.webp' },
    { key: 'aguacate', year: 2019, color: '#A39B4B', imgExt: IMG + 'banner_aguacate_ext.webp', imgInt: IMG + 'banner_aguacate_int.webp' },
    { key: 'leanri', year: 2019, color: '#004434', imgExt: IMG + 'banner_leanri_ext.webp', imgInt: IMG + 'banner_leanri_int.webp' },
    { key: 'sando', year: 2018, color: '#A39B4B', imgExt: IMG + 'banner_sando_ext.webp', imgInt: IMG + 'banner_sando_int.webp' },
    { key: 'm7', year: 2016, color: '#004434', imgExt: IMG + 'banner_m7_ext.webp', imgInt: IMG + 'banner_m7_int.webp' },
    { key: 'soreli', year: 2014, color: '#A39B4B', imgExt: IMG + 'banner_soreli_ext.webp', imgInt: IMG + 'banner_soreli_int.webp' },
    { key: 'arra', year: 2012, color: '#004434', imgExt: IMG + 'banner_arra_ext.webp', imgInt: IMG + 'banner_arra_int.webp' },
    { key: 'orri', year: 2010, color: '#A39B4B', imgExt: IMG + 'banner_orri_ext.webp', imgInt: IMG + 'banner_orri_int.webp' },
    { key: 'citrogold', year: 2009, color: '#004434', imgExt: IMG + 'banner_citrogold_ext.webp', imgInt: IMG + 'banner_citrogold_int.webp' },
    { key: 'grapa', year: 2007, color: '#A39B4B', imgExt: IMG + 'banner_grapa_ext.webp', imgInt: IMG + 'banner_grapa_int.webp' },
    { key: 'nadorcott', year: 2004, color: '#004434', imgExt: IMG + 'banner_nadorcott_ext.webp', imgInt: IMG + 'banner_nadorcott_int.webp' },
  ];

  modalOpen = false; // .four
  modalOut = false; // .out
  currentIndex = 0;
  private busy = false;

  @ViewChild('modalEl') private modalEl?: ElementRef<HTMLElement>;
  @ViewChild('timeline') private timeline?: ElementRef<HTMLElement>;
  private io?: IntersectionObserver;

  get current(): Story {
    return this.stories[this.currentIndex];
  }
  get hasPrev(): boolean {
    return this.currentIndex > 0;
  }
  get hasNext(): boolean {
    return this.currentIndex < this.stories.length - 1;
  }

  ngAfterViewInit(): void {
    // Animación de entrada de las tarjetas del timeline al hacer scroll.
    const items = this.timeline?.nativeElement.querySelectorAll('.tl-item');
    if (items) {
      this.io = new IntersectionObserver(
        (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('in-view'); }),
        { threshold: 0.25 },
      );
      items.forEach((el) => this.io!.observe(el));
    }
  }

  // tx: '0' | '-190%' | '170%' — manipula el elemento directamente para conservar el timing original
  private setTransform(tx: string, withTransition: boolean): void {
    const el = this.modalEl?.nativeElement;
    if (!el) return;
    el.classList.toggle('no-transition', !withTransition);
    el.style.transform = `translateX(${tx})`;
    if (!withTransition) void el.offsetWidth; // fuerza reflow
  }

  openModal(index: number): void {
    this.currentIndex = index;
    this.modalOut = false;
    this.modalOpen = true;
    document.body.classList.add('modal-active');
    requestAnimationFrame(() => {
      this.setTransform('170%', false);
      requestAnimationFrame(() => requestAnimationFrame(() => this.setTransform('0', true)));
    });
  }

  closeModal(): void {
    if (this.busy) return;
    this.setTransform('-190%', true);
    setTimeout(() => {
      this.modalOpen = false;
      this.modalOut = true;
      document.body.classList.remove('modal-active');
    }, 1000);
    setTimeout(() => (this.modalOut = false), 2000);
  }

  next(): void {
    if (this.busy || !this.hasNext) return;
    this.busy = true;
    this.setTransform('-190%', true);
    setTimeout(() => {
      this.currentIndex++;
      this.setTransform('170%', false);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.setTransform('0', true);
        this.busy = false;
      }));
    }, 1000);
  }

  prev(): void {
    if (this.busy || !this.hasPrev) return;
    this.busy = true;
    this.setTransform('170%', true);
    setTimeout(() => {
      this.currentIndex--;
      this.setTransform('-190%', false);
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.setTransform('0', true);
        this.busy = false;
      }));
    }, 1000);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.modalOpen) this.closeModal();
  }

  ngOnDestroy(): void {
    this.io?.disconnect();
    document.body.classList.remove('modal-active');
  }
}
